<template>
  <template v-if="isVisible">
    <WizardModal
      ref="wizardModalRef"
      :show-modal="isVisible"
      :modal-title="modalTitle"
      size="large"
      :steps="steps"
      :linear="true"
      :allow-step-click="allowStepClick"
      :custom-buttons="true"
      :next-label="getNextLabel"
      :disable-next="!canProceed"
      :loading="isLoading || isSaving"
      @before-step-change="(handleBeforeStepChange as any)"
      @finish="handleFinish"
      @cancel="handleClose"
      @close="handleClose"
    >
      <!-- Step 0: Account Form (Create/Edit) -->
      <template #step-0>
        <FranchisesSubaccountsFormStep
          ref="accountFormRef"
          :mode="mode"
          :account-id="accountId ?? undefined"
          :provider-code="providerCode ?? undefined"
          :account-data="accountData ?? undefined"
          @validate="handleStepValidation(0, $event)"
        />
      </template>

      <!-- Step 1: Configuration Settings -->
      <template #step-1>
        <FranchisesConfigStep
          ref="configFormRef"
          :account-id="savedAccountId ?? undefined"
          :config-data="configData ?? undefined"
          @validate="handleStepValidation(1, $event)"
        />
      </template>

      <!-- Step 2: Fiscal Configuration -->
      <template #step-2>
        <FranchisesFiscalStep
          :account-id="savedAccountId ?? undefined"
          @validate="handleStepValidation(2, $event)"
        />
      </template>
    </WizardModal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import FranchisesSubaccountsFormStep from './wizard/FranchisesSubaccountsFormStep.vue';
import FranchisesConfigStep from './wizard/FranchisesConfigStep.vue';
import FranchisesFiscalStep from './wizard/FranchisesFiscalStep.vue';
import { useFranchisesSubaccounts } from '@/composables/useFranchisesSubaccounts';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import type { SubaccountsFranchises_CriarSubcontaInput } from '@/contracts/SubaccountsFranchises_CriarSubcontaInput';
import type { SubaccountsFranchises_AtualizarConfigInput } from '@/contracts/SubaccountsFranchises_AtualizarConfigInput';
import type { SubaccountsFranchises_AtualizarInput } from '@/contracts/SubaccountsFranchises_AtualizarInput';

type Id = number | string;
type WizardMode = 'create' | 'edit';

interface AccountData {
  id?: Id;
  config?: Record<string, unknown>;
  fiscal_config?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ModalOpenPayload {
  mode?: WizardMode;
  account_id?: Id | null;
  provider_code?: string | null;
}

interface AccountFormStepInstance {
  validate?: () => boolean;
  getFormData?: () => SubaccountsFranchises_CriarSubcontaInput | null;
}

interface ConfigFormStepInstance {
  validate?: () => boolean;
  getFormData?: () => SubaccountsFranchises_AtualizarConfigInput | null;
}

interface WizardStep {
  title: string;
  description: string;
  key: string;
  validate: () => boolean;
}

interface BeforeStepChangePayload {
  from: number;
  to: number;
  prevented?: boolean;
}

interface WizardModalInstance {
  currentStep?: { value?: number };
  goToStep?: (step: number) => void;
}

const { t } = useI18n();
const { getById, create, update, updateConfig } = useFranchisesSubaccounts();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const skipNextInterception = ref<boolean>(false); // Flag to skip before-step-change interception after save
const mode = ref<WizardMode>('create'); // 'create' or 'edit'
const accountId = ref<Id | null>(null);
const providerCode = ref<string | null>(null);
const savedAccountId = ref<Id | null>(null); // Account ID after save (for config/fiscal steps)

// Form refs
const wizardModalRef = ref<WizardModalInstance | null>(null);
const accountFormRef = ref<AccountFormStepInstance | null>(null);
const configFormRef = ref<ConfigFormStepInstance | null>(null);

// Data
const accountData = ref<AccountData | null>(null);
const configData = ref<Record<string, unknown> | null>(null);

// Step validation state
const stepValidation = ref<Record<number, boolean>>({
  0: false, // Account form
  1: true,  // Config (optional)
  2: true   // Fiscal (optional)
});

// Computed
const modalTitle = computed<string>(() => {
  if (mode.value === 'create') {
    return t('Nova Subconta de Pagamento');
  }
  return t('Editar Subconta de Pagamento');
});

const steps = computed<WizardStep[]>(() => {
  const baseSteps: WizardStep[] = [
    {
      title: t('Dados da Conta'),
      description: t('Informações básicas da subconta'),
      key: 'account',
      validate: () => stepValidation.value[0]
    },
    {
      title: t('Configurações'),
      description: t('Opções de pagamento e notificações'),
      key: 'config',
      validate: () => stepValidation.value[1]
    },
    {
      title: t('Fiscal'),
      description: t('Configuração para emissão de notas'),
      key: 'fiscal',
      validate: () => stepValidation.value[2]
    }
  ];
  return baseSteps;
});

const allowStepClick = computed<boolean>(() => {
  // Only allow clicking on steps after the account is saved
  return !!savedAccountId.value;
});

const canProceed = computed<boolean>(() => {
  const currentStep = wizardModalRef.value?.currentStep?.value ?? 0;
  return stepValidation.value[currentStep] !== false;
});

// Methods
const getNextLabel = (currentStep: number): string => {
  const isLastStep = currentStep === steps.value.length - 1;

  if (currentStep === 0 && !savedAccountId.value) {
    return mode.value === 'create' ? t('Criar e Continuar') : t('Salvar e Continuar');
  }

  if (isLastStep) {
    return t('Finalizar');
  }

  return t('Próximo');
};

const handleStepValidation = (stepIndex: number, isValid: boolean): void => {
  stepValidation.value[stepIndex] = isValid;
};

const resetState = (): void => {
  mode.value = 'create';
  accountId.value = null;
  providerCode.value = null;
  savedAccountId.value = null;
  accountData.value = null;
  configData.value = null;
  stepValidation.value = { 0: false, 1: true, 2: true };
  isSaving.value = false;
  isLoading.value = false;
  skipNextInterception.value = false;
};

const loadAccountData = async (): Promise<void> => {
  if (!accountId.value) return;

  log('FranchisesSubaccountsWizard', 'loadAccountData', 'Loading account data', accountId.value);
  isLoading.value = true;

  try {
    const data = (await getById(Number(accountId.value))) as unknown as AccountData;
    log('FranchisesSubaccountsWizard', 'loadAccountData', 'Loaded data', data);

    // Set account data for form
    accountData.value = data;

    // Extract config data
    configData.value = (data.config as Record<string, unknown>) || {};

    // Mark step 0 as valid since we have existing data
    stepValidation.value[0] = true;
  } catch (err) {
    log('FranchisesSubaccountsWizard', 'loadAccountData', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados da subconta'));
  } finally {
    isLoading.value = false;
  }
};

const openModal = async (data: ModalOpenPayload = {}): Promise<void> => {
  log('FranchisesSubaccountsWizard', 'openModal', 'Opening wizard', data);

  // Reset state
  resetState();

  // Set mode and initial data
  mode.value = data.mode || 'create';
  accountId.value = data.account_id || null;
  providerCode.value = data.provider_code || null;

  // If editing, load full account data
  if (mode.value === 'edit' && accountId.value) {
    await loadAccountData();
    savedAccountId.value = accountId.value;
  }

  isVisible.value = true;
};

const saveAccountData = async (): Promise<AccountData> => {
  log('FranchisesSubaccountsWizard', 'saveAccountData', 'Saving account data');

  // Get form data from step component
  const formData = accountFormRef.value?.getFormData?.();
  if (!formData) {
    throw new Error(t('Dados do formulário não disponíveis'));
  }

  // Validate form
  const isValid = accountFormRef.value?.validate?.();
  if (!isValid) {
    throw new Error(t('Preencha todos os campos obrigatórios'));
  }

  let result: AccountData;

  if (mode.value === 'create') {
    // Create new subaccount
    result = (await create(formData)) as unknown as AccountData;
    savedAccountId.value = result.id ?? null;
    eventBus.emit('globalMessage.success', t('Subconta criada com sucesso'));
    eventBus.emit('franchisessubaccount.created', result);
  } else {
    // Update existing subaccount — map CriarSubcontaInput fields to AtualizarInput
    // (both share the same string fields; address is string in the form and accepted
    //  as-is by the backend even though AtualizarInput types it as unknown[])
    const updatePayload: SubaccountsFranchises_AtualizarInput = {
      name: formData.name,
      email: formData.email,
      document: formData.document,
      phone: formData.phone,
      mobilePhone: formData.mobilePhone,
      addressNumber: formData.addressNumber,
      complement: formData.complement ?? undefined,
      province: formData.province,
      postalCode: formData.postalCode,
      franchise_ids: formData.franchise_ids,
    };
    result = (await update(savedAccountId.value as unknown as Id, updatePayload)) as unknown as AccountData;
    eventBus.emit('globalMessage.success', t('Subconta atualizada com sucesso'));
    eventBus.emit('franchisessubaccount.updated', result);
  }

  log('FranchisesSubaccountsWizard', 'saveAccountData', 'Account saved', result);
  return result;
};

const saveConfigData = async (): Promise<unknown> => {
  if (!savedAccountId.value) {
    log('FranchisesSubaccountsWizard', 'saveConfigData', 'No account ID, skipping config save');
    return undefined;
  }

  log('FranchisesSubaccountsWizard', 'saveConfigData', 'Saving config data');

  const formData = configFormRef.value?.getFormData?.();
  if (!formData) {
    log('FranchisesSubaccountsWizard', 'saveConfigData', 'No config data to save');
    return undefined;
  }

  const result = await updateConfig(savedAccountId.value, formData.config);
  eventBus.emit('globalMessage.success', t('Configurações salvas com sucesso'));

  return result;
};

const handleBeforeStepChange = async (event: BeforeStepChangePayload): Promise<void> => {
  const fromStep = event.from;
  const toStep = event.to;
  const currentStepKey = steps.value[fromStep]?.key;

  // Skip interception if flag is set (we're manually advancing after save)
  if (skipNextInterception.value) {
    skipNextInterception.value = false;
    return;
  }

  // Only intercept forward navigation that requires saving
  if (toStep <= fromStep) {
    // Allow backward navigation without blocking
    return;
  }

  log('FranchisesSubaccountsWizard', 'handleBeforeStepChange', 'Step change requested', {
    fromStep,
    toStep,
    currentStepKey,
    savedAccountId: savedAccountId.value
  });

  // Prevent automatic step advancement - we'll do it manually after save
  event.prevented = true;

  isSaving.value = true;

  try {
    switch (currentStepKey) {
      case 'account':
        // Save account data first
        await saveAccountData();
        break;

      case 'config':
        // Save config data
        await saveConfigData();
        break;

      // 'fiscal' step is a pointer panel to the canonical fiscal-config screen —
      // it never saves anything, so there is no case to intercept here.
    }

    // Set flag to skip next interception, then manually advance
    skipNextInterception.value = true;
    wizardModalRef.value?.goToStep?.(toStep);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('FranchisesSubaccountsWizard', 'handleBeforeStepChange', 'Error', error);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar dados'));
  } finally {
    isSaving.value = false;
  }
};

const handleClose = (): void => {
  log('FranchisesSubaccountsWizard', 'handleClose', 'Closing wizard');
  isVisible.value = false;
  resetState();
};

const handleFinish = async (): Promise<void> => {
  log('FranchisesSubaccountsWizard', 'handleFinish', 'Finishing wizard');

  isSaving.value = true;

  try {
    // The fiscal step is a pointer panel to the canonical fiscal-config screen —
    // it never returns a payload, so there is nothing to save on finish.
    eventBus.emit('globalMessage.success', t('Subconta configurada com sucesso'));
    handleClose();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('FranchisesSubaccountsWizard', 'handleFinish', 'Error', error);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar configuração'));
  } finally {
    isSaving.value = false;
  }
};

// Event handlers
const handleModalOpen = (data: ModalOpenPayload): void => {
  log('FranchisesSubaccountsWizard', 'handleModalOpen', 'Modal open event received', data);
  openModal(data);
};

// Lifecycle
onMounted(() => {
  log('FranchisesSubaccountsWizard', 'onMounted', 'Component mounted');

  // Listen for wizard open events
  eventBus.on('modal.franchisessubaccount.wizard.open', handleModalOpen as (data: unknown) => void);
  eventBus.on('modal.franchisessubaccount.wizard.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.franchisessubaccount.wizard.open', handleModalOpen as (data: unknown) => void);
  eventBus.off('modal.franchisessubaccount.wizard.close', handleClose);
});

// Expose methods
defineExpose({
  open: openModal,
  close: handleClose
});
</script>

<style scoped>
/* Wizard-specific styles are handled by WizardModal */
</style>
